import React, {FC} from 'react';
import {connect} from 'react-redux';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CustomFieldAttribute} from '../../../models/api/customFieldAttributeModel';
import {CustomFieldResourceTypesEnum} from '../../../models/api/customFieldModel';
import {fetchCustomFieldsForResourceTypeAsyncAction} from '../../../store/data/customFields/customFieldsActions';
import {getCustomFieldsForAccountsFlat} from '../../../store/data/customFields/customFieldsSelectors';
import {getCustomFields} from '../../../store/data/dataSelectors';
import {getFlatInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {getFlatTeammates} from '../../../store/data/teammates/teammatesSelectors';
import {ContactCardDetailsCustomFields} from './contactCardDetailsCustomFields';

/*
 * Props.
 */

interface ContactCardDetailsCustomFieldsContainerProps {
  customAttributes: ReadonlyArray<CustomFieldAttribute>;
  availableItemsToRender: number;
  account?: AccountModel;
}

type ContactCardDetailsCustomFieldsContainerComponentProps = ContactCardDetailsCustomFieldsContainerProps &
  ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

/*
 * Selectors.
 */

function makeGetAreAccountCustomFieldsLoaded() {
  return createSelector([getCustomFieldsForAccountsFlat], (customFields) => customFields.length > 0);
}

/*
 * Component.
 */

const ContactCardDetailsCustomFieldsContainerComponent: FC<
  ContactCardDetailsCustomFieldsContainerComponentProps
> = (props) => {
  const {
    customFields,
    customAttributes,
    availableItemsToRender,
    teammates,
    inboxes,
    areAccountCustomFieldsLoaded,
    account,
    fetchCustomFieldsForResourceTypeAsync,
  } = props;

  // We may not have account custom fields loaded for accounts so we should check if we should fetch them.
  useInitialEffect(() => {
    if (account && (account.customFieldAttributes ?? []).length > 0 && !areAccountCustomFieldsLoaded) {
      fetchCustomFieldsForResourceTypeAsync(CustomFieldResourceTypesEnum.ACCOUNT);
    }
  });

  return (
    <ContactCardDetailsCustomFields
      customFields={customFields}
      customAttributes={customAttributes}
      availableItemsToRender={availableItemsToRender}
      teammates={teammates}
      inboxes={inboxes}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  customFields: getCustomFields,
  teammates: getFlatTeammates,
  inboxes: getFlatInboxes,
  areAccountCustomFieldsLoaded: makeGetAreAccountCustomFieldsLoaded(),
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  fetchCustomFieldsForResourceTypeAsync: fetchCustomFieldsForResourceTypeAsyncAction,
}));

export const ContactCardDetailsCustomFieldsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactCardDetailsCustomFieldsContainerComponent);
